import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface StripePaymentFormProps {
 onSuccess: () => void;
 onError: (error: string) => void;
 subscriptionId?: string | null;
}

export default function StripePaymentForm({ onSuccess, onError, subscriptionId }: StripePaymentFormProps) {
 const stripe = useStripe();
 const elements = useElements();
 const [processing, setProcessing] = useState(false);
 const [localError, setLocalError] = useState<string | null>(null);

 const handleSubmit = async (event: React.FormEvent) => {
 event.preventDefault();
 setLocalError(null);

 if (!stripe || !elements) {
 return;
 }

 setProcessing(true);

 const { error, paymentIntent } = await stripe.confirmPayment({
 elements,
 redirect: 'if_required',
 confirmParams: {
 // Return URL necessária caso o banco redirecione (ex: 3D Secure)
 return_url: window.location.origin + "/subscription?success=true",
 },
 });

 if (error) {
 setProcessing(false);
 // 🚨 PAGAMENTO RECUSADO: Nunca chamar onSuccess aqui
 // O Stripe retornou um erro — cartão recusado, saldo insuficiente, etc.
 const friendlyMessage = getFriendlyErrorMessage(error.code, error.message);
 setLocalError(friendlyMessage);
 onError(friendlyMessage);
 console.error("Stripe payment error:", error.code, error.message);
 return;
 }

 if (paymentIntent) {
 if (paymentIntent.status === "succeeded") {
 setProcessing(false);
 if (subscriptionId) {
   supabase.auth.getUser().then(({ data }) => {
     if (data?.user?.email) {
       supabase.functions.invoke("fix-subscription", { body: { email: data.user.email } }).catch(console.warn);
     }
   }).catch(console.warn);
 }
 onSuccess();
 } else if (paymentIntent.status === "requires_action") {
 // O Stripe vai mostrar a UI (ex: QR Code do Pix)
 console.log("Pagamento requer ação adicional (Pix/3D Secure). Iniciando verificação...");
 
 // Inicia um polling para verificar quando o usuário pagar o Pix
 const clientSecret = paymentIntent.client_secret;
 if (clientSecret) {
   const pollInterval = setInterval(async () => {
     const { paymentIntent: updatedIntent } = await stripe.retrievePaymentIntent(clientSecret);
     if (updatedIntent && updatedIntent.status === 'succeeded') {
       clearInterval(pollInterval);
       setProcessing(false);
       if (subscriptionId) {
         supabase.auth.getUser().then(({ data }) => {
           if (data?.user?.email) {
             supabase.functions.invoke("fix-subscription", { body: { email: data.user.email } }).catch(console.warn);
           }
         }).catch(console.warn);
       }
       onSuccess();
     } else if (updatedIntent && (updatedIntent.status === 'canceled' || updatedIntent.status === 'requires_payment_method')) {
       clearInterval(pollInterval);
       setProcessing(false);
       const msg = "Pagamento cancelado ou falhou. Tente novamente.";
       setLocalError(msg);
       onError(msg);
     }
   }, 3000); // Checa a cada 3 segundos
 }
 
 } else if (
 paymentIntent.status === "requires_payment_method" ||
 paymentIntent.status === "canceled"
 ) {
 setProcessing(false);
 const msg = "Pagamento não foi completado. Por favor, verifique seus dados e tente novamente.";
 setLocalError(msg);
 onError(msg);
 } else {
 setProcessing(false);
 console.warn("Status de pagamento inesperado:", paymentIntent.status);
 const msg = `Status do pagamento: ${paymentIntent.status}. Aguarde a confirmação ou tente novamente.`;
 setLocalError(msg);
 }
 }
 };

 return (
 <form onSubmit={handleSubmit} className="space-y-6">
 <div className="bg-background/50 rounded-lg p-6">
 <PaymentElement options={{ 
 layout: "tabs",
 wallets: { applePay: 'auto', googlePay: 'auto' } 
 }} />
 </div>

 {/* 🚨 Exibir erro de pagamento diretamente no formulário */}
 {localError && (
 <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
 <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
 <div>
 <p className="text-sm font-semibold text-red-400">Pagamento Recusado</p>
 <p className="text-xs text-red-300/80 mt-1">{localError}</p>
 <p className="text-xs text-muted-foreground mt-2">
 Verifique os dados do cartão e tente novamente, ou use outro método de pagamento.
 </p>
 </div>
 </div>
 )}

 <Button
 className="w-full text-lg py-6 btn-primary"
 disabled={processing || !stripe || !elements}
 >
 {processing ? "Processando..." : "Confirmar Pagamento Seguro"}
 </Button>

 <div className="text-center text-xs text-muted-foreground mt-4 space-x-2">
 <span>🔒 Pagamento blindado e protegido por Stripe 256-bit SSL</span>
 </div>
 </form>
 );
}

/**
 * Traduz os códigos de erro do Stripe para mensagens amigáveis em português.
 */
function getFriendlyErrorMessage(code?: string, fallback?: string): string {
 const messages: Record<string, string> = {
 card_declined: "Cartão recusado. Por favor, verifique os dados ou use outro cartão.",
 insufficient_funds: "Saldo insuficiente no cartão. Use outro cartão ou aguarde.",
 incorrect_cvc: "Código de segurança (CVV) incorreto. Verifique e tente novamente.",
 expired_card: "Cartão vencido. Use um cartão com data de validade válida.",
 incorrect_number: "Número do cartão incorreto. Verifique e tente novamente.",
 invalid_expiry_month: "Mês de validade inválido.",
 invalid_expiry_year: "Ano de validade inválido.",
 invalid_cvc: "CVV inválido.",
 processing_error: "Erro no processamento. Tente novamente em alguns instantes.",
 do_not_honor: "Banco recusou a transação. Entre em contato com seu banco.",
 lost_card: "Cartão bloqueado pelo banco.",
 stolen_card: "Cartão bloqueado pelo banco.",
 authentication_required: "Sua conta requer autenticação adicional. Verifique seu aplicativo bancário.",
 };
 return messages[code || ""] || fallback || "Erro ao processar pagamento. Tente novamente.";
}
